import { type DefaultContext, gqlPromise } from '@egw/clients'
import { type WealthOverview } from '../../models'

export const getWealthOverview = async (context?: DefaultContext): Promise<WealthOverview> => {
  const response = (await gqlPromise(
    {
      name: 'wealthOverview',
      query: `
        query wealthOverview {
          wealthOverview {
            netWorth {
              amount
              currencyCode
            }

            highYieldChecking {
              productName
              balance {
                total {
                  amount
                  currencyCode
                }
                available {
                  amount
                  currencyCode
                }
              }

              accounts {
                accountTitle
                attributes {
                  minimumDeposit
                  minimumDepositMet
                }
                accountType
                alias
                apy
                status
                accountName
                routingNumber
                accountNumber
                debitCard {
                  cardId
                  cardName
                  maskedPAN
                  cardStatus
                  expDate
                  pinSelection
                  needToSetPin
                  cardStatusReason
                }
                balance {
                  total {
                    amount
                    currencyCode
                  }
                  available {
                    amount
                    currencyCode
                  }
                }

                achLimits {
                  ... on AchLimits {
                    __typename
                    credit {
                      singleTransactionLimit
                      dailyLimit
                      monthlyLimit
                    }
                    debit {
                      singleTransactionLimit
                      dailyLimit
                      monthlyLimit
                    }
                  }
                  ... on AchLimitsError {
                    __typename
                    errorType
                  }
                }
              }
            }

            highYieldSavings {
              productName
              balance {
                total {
                  amount
                  currencyCode
                }
                available {
                  amount
                  currencyCode
                }
              }

              accounts {
                attributes {
                  minimumDeposit
                  minimumDepositMet
                }
                accountType
                alias
                apy
                status
                accountName
                routingNumber
                accountNumber
                balance {
                  total {
                    amount
                    currencyCode
                  }
                  available {
                    amount
                    currencyCode
                  }
                }

                achLimits {
                  ... on AchLimits {
                    __typename
                    credit {
                      singleTransactionLimit
                      dailyLimit
                      monthlyLimit
                    }
                    debit {
                      singleTransactionLimit
                      dailyLimit
                      monthlyLimit
                    }
                  }
                  ... on AchLimitsError {
                    __typename
                    errorType
                  }
                }
              }
            }

            personalPortfolio {
              productName
              balance {
                accountValue {
                  amount
                  currencyCode
                }
                investedBalance {
                  amount
                  currencyCode
                }
                cashBalance {
                  amount
                  currencyCode
                }
                pendingWithdrawal {
                  amount
                  currencyCode
                }
                availableForWithdrawal {
                  amount
                  currencyCode
                }
              } 
              accounts {
                alias
                accountNumber
                accountName
                status
                balance {
                  investedBalance {
                    amount
                    currencyCode
                  }
                  accountValue {
                    amount
                    currencyCode
                  }
                  cashBalance {
                    amount
                    currencyCode
                  }
                  pendingWithdrawal {
                    amount
                    currencyCode
                  }
                  availableForWithdrawal {
                    amount
                    currencyCode
                  }
                  amountSinceInception {
                    amount
                    currencyCode
                  }
                }
                ... on InvestmentBrokerageAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }
                ... on InvestmentIRAAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                  }
                  retirementContribution {
                    annualContributionsLimit
                    remainingAnnualLimit
                  }
                }
                ... on InvestmentRothIRAAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                  }
                  retirementContribution {
                    annualContributionsLimit
                    remainingAnnualLimit
                  }
                }
                ... on InvestmentBrokerageCompanionAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  }
                }
                ... on InvestmentIRACompanionAccount {
                  __typename
                  accountTitle
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  }
                }
                ... on InvestmentRothIRACompanionAccount {
                  __typename
                  accountTitle
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  }
                }
                ... on InvestmentInheritedIRAAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }
                ... on InvestmentInheritedRothIRAAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }
                
                ... on InvestmentJointBrokerageTenantsInCommonAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }

                ... on InvestmentJointBrokerageTenantsInCommonCompanionAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  }
                }

                ... on InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }

                ... on InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  }
                }

                ... on InvestmentJointBrokerageTenantsByEntiretyAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }

                ... on InvestmentJointBrokerageTenantsByEntiretyCompanionAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  } 
                }

                ... on InvestmentJointBrokerageCommunityPropertyAccount {
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                }

                ... on InvestmentJointBrokerageCommunityPropertyCompanionAccount { 
                  __typename
                  accountTitle
                  attributes {
                    minimumDeposit
                    minimumDepositMet
                    acatAllowed
                    clientWithdrawalAllowed
                    clientDepositAllowed
                    iraWithdrawal {
                      withdrawalAmount
                      distributionType
                    }
                  }
                  holdings {
                    name
                    symbol
                    costBasis {
                      amount
                      currencyCode
                    }
                    amount {
                      amount
                      currencyCode
                    }
                    quantity
                  }
                }

              }
            }
          }
        }
      `,
    },
    context,
  )) as { wealthOverview: WealthOverview }
  return response.wealthOverview
}
